import { Navigate, createBrowserRouter, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import ProjectFunds from '../../pages/Project/Funds';
import OutputTables from '../../pages/OutputTables';
import Reportings from '../../pages/Dashboard/Reportings';
import SelectFiles from '../../pages/Dashboard/Reportings/SelectFiles';
import FCAReport from '../../pages/Report/FCA';
import CreateProject from '../../pages/Project/Create';
import Project from '../../pages/Project';
import Template from '../../pages/Project/Template';
import Quarters from '../../pages/Project/Quarters';
import DataExtraction from '../../pages/DataExtraction';
import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import UploadFiles from '../../pages/Project/UploadFiles';
import ESMAReport from '../../pages/Report/ESMA';
import AddAIFs from '../../pages/Project/Quarters/AddAIFs';
import AddFile from '../../pages/Project/Quarters/AddFile';
import Jurisdictions from '../../pages/Project/Quarters/Jurisdictions';
import { ManageUsers } from '../../pages/ManageUsers/index';
import { PreviousLocationProvider } from '../../providers/PreviousLocationProvider';
import ErrorBoundary from '../../components/ErrorBoundary';
import SignalRDemo from '../../pages/SignalRDemo';
import ActivityLog from '../../pages/ActivityLog';
import FormPF from '../../pages/Report/FormPF';
import IntegrationsPage from '../../pages/Integrations';
import { useUserCompaniesCurrentUserAdminState } from '../../services/user.service';
import { USER_ROLES } from '../../consts/userRoles';
import { NoAccess } from '../../components/NoAccess';
import { workflowService } from '../../services/workflow.services';
import toast from 'react-hot-toast';

const withPreviousLocation = element => (
  <PreviousLocationProvider>{element}</PreviousLocationProvider>
);

const AdminRoute = ({ children }) => {
  const { data: currentUserState, isLoading } = useUserCompaniesCurrentUserAdminState();

  if (isLoading) {
    return <div id="admin-route-loading">Loading...</div>;
  }

  if (currentUserState?.role !== USER_ROLES.ADMIN && !currentUserState?.is_super_admin) {
    return <Navigate to="/project" replace />;
  }

  return children;
};

const WorkflowAccessRoute = ({ children }) => {
  const { id, projectId, workflowId } = useParams();
  const navigate = useNavigate();
  const targetWorkflowId = workflowId || projectId || id;
  const { isLoading, isError, error } = useQuery(
    ['WORKFLOW_ACCESS_GUARD', targetWorkflowId],
    () => workflowService.getWorkflowById(targetWorkflowId),
    {
      cacheTime: 0,
      enabled: !!targetWorkflowId,
      retry: false,
      staleTime: 0
    }
  );

  const shouldRedirect = isError && [403, 404].includes(error?.response?.status);

  useEffect(() => {
    if (shouldRedirect) {
      toast.error('You do not have access to that project.');
      navigate('/project', { replace: true });
    }
  }, [navigate, shouldRedirect]);

  if (!targetWorkflowId) {
    return children;
  }

  if (isLoading) {
    return <div id="workflow-access-route-loading">Loading...</div>;
  }

  if (shouldRedirect) {
    return null;
  }

  return children;
};

export const privateRoutes = createBrowserRouter([
  {
    path: '/activity-log',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <ErrorBoundary componentName="ActivityLog">
            <ActivityLog />
          </ErrorBoundary>
        )
      }
    ]
  },
  {
    path: '/data-extraction/:id/:periodId',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        index: true,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="DataExtraction">
              <DataExtraction />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      }
    ]
  },
  {
    path: '/',
    element: withPreviousLocation(<Layout />),
    children: [
      {
        path: 'project',
        children: [
          {
            index: true,
            element: (
              <ErrorBoundary componentName="Project">
                <Project />
              </ErrorBoundary>
            )
          },
          {
            path: 'quaters/:id',
            children: [
              {
                index: true,
                element: (
                  <WorkflowAccessRoute>
                    <Quarters />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: ':periodId/add-aifs',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowAccessRoute>
                        <AddAIFs />
                      </WorkflowAccessRoute>
                    )
                  }
                ]
              },
              {
                path: ':periodId/add-file',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowAccessRoute>
                        <AddFile />
                      </WorkflowAccessRoute>
                    )
                  }
                ]
              },
              {
                path: ':periodId/upload-files',
                children: [
                  {
                    index: true,
                    element: (
                      <WorkflowAccessRoute>
                        <UploadFiles />
                      </WorkflowAccessRoute>
                    )
                  }
                ]
              },
              {
                path: ':periodId/jurisdictions',
                element: (
                  <WorkflowAccessRoute>
                    <Jurisdictions />
                  </WorkflowAccessRoute>
                )
              }
            ]
          },
          {
            path: ':projectId',
            children: [
              {
                index: true,
                element: (
                  <WorkflowAccessRoute>
                    <Template />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: 'funds',
                element: (
                  <WorkflowAccessRoute>
                    <ProjectFunds />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: 'jurisdictions',
                element: (
                  <WorkflowAccessRoute>
                    <Jurisdictions />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: 'create',
                element: (
                  <WorkflowAccessRoute>
                    <CreateProject isCreate={true} />
                  </WorkflowAccessRoute>
                )
              },
              {
                path: ':templateId',
                element: (
                  <WorkflowAccessRoute>
                    <CreateProject />
                  </WorkflowAccessRoute>
                )
              }
            ]
          }
        ]
      },

      {
        path: 'dashboard',
        element: <Layout />,
        children: [
          {
            index: true,
            // element: <Dashboard />,
            element: <Navigate to="/project" />
          },
          {
            path: 'reportings',
            children: [
              {
                index: true,
                element: (
                  <ErrorBoundary componentName="Reportings">
                    <Reportings />
                  </ErrorBoundary>
                )
              },
              {
                path: 'report',
                children: [
                  {
                    path: ':reportId',
                    element: (
                      <ErrorBoundary componentName="FCAReport">
                        <FCAReport />
                      </ErrorBoundary>
                    )
                  }
                ]
              },
              {
                path: ':integration',
                children: [
                  {
                    path: 'files',
                    element: <SelectFiles />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: `report/fca/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="FCAReport">
              <FCAReport />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: `report/esma/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="ESMAReport">
              <ESMAReport />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: `report/pf/:reportingMemberState/:workflowId/:periodId`,
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="FormPF">
              <FormPF />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: 'integrations',
        element: <IntegrationsPage />
      },
      {
        path: 'output-tables/:projectId/:periodId',
        element: (
          <WorkflowAccessRoute>
            <ErrorBoundary componentName="OutputTables">
              <OutputTables />
            </ErrorBoundary>
          </WorkflowAccessRoute>
        )
      },
      {
        path: 'templates',
        element: <>Saved templates</>
      },
      {
        path: 'Payment',
        element: <>Payment</>
      },
      {
        path: 'help',
        element: <>Help</>
      },
      {
        path: 'access-denied',
        element: <NoAccess />
      },
      {
        path: 'settings',
        element: (
          <AdminRoute>
            <ManageUsers />
          </AdminRoute>
        )
      },
      {
        path: 'signalr-demo',
        element: <SignalRDemo />
      },
      {
        path: '*',
        element: <Navigate to="/project" />
      },
      {
        index: true,
        element: <Navigate to="/project" />
      },
      {}
    ]
  },
  {
    path: '*',
    element: <Navigate to="/project" />
  }
]);
